import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { listTemplesForMap, listVisitTempleIds } from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n, pickName } from "@/lib/i18n";
import { MapPin, Navigation } from "lucide-react";

type Temple = {
  id: string;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  yatra_order: number;
  latitude: number | null;
  longitude: number | null;
};

export const Route = createFileRoute("/app/map")({ component: MapScreen });

function MapScreen() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [temples, setTemples] = useState<Temple[]>([]);
  const [visited, setVisited] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      setTemples(listTemplesForMap());
      if (user) {
        setVisited(new Set(listVisitTempleIds(user.id)));
      }
    })();
  }, [user]);

  const allCoords = temples.filter((tp) => tp.latitude && tp.longitude);
  const center = allCoords.length
    ? {
        lat: allCoords.reduce((a, b) => a + Number(b.latitude), 0) / allCoords.length,
        lng: allCoords.reduce((a, b) => a + Number(b.longitude), 0) / allCoords.length,
      }
    : { lat: 23.0225, lng: 72.5714 };
  const mapsAll = `https://www.google.com/maps/dir/${allCoords.map((tp) => `${tp.latitude},${tp.longitude}`).join("/")}`;

  return (
    <div className="px-5 pt-16">
      <div className="mb-3">
        <span className="font-serif text-xl font-bold">{t("map")}</span>
      </div>
      <div className="overflow-hidden rounded-2xl border border-border">
        <iframe
          title="Yatra Map"
          width="100%"
          height="280"
          style={{ border: 0 }}
          loading="lazy"
          src={`https://maps.google.com/maps?q=${center.lat},${center.lng}&z=11&output=embed`}
        />
      </div>
      <a
        href={mapsAll}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary mt-3 w-full"
      >
        <Navigation className="h-4 w-4" /> Open Full Yatra Route
      </a>

      <div className="mt-4 space-y-2">
        {temples.map((tp) => {
          const done = visited.has(tp.id);
          return (
            <Link
              key={tp.id}
              to="/app/temples/$id"
              params={{ id: tp.id }}
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-3"
            >
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"}`}
              >
                {tp.yatra_order}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{pickName(tp, lang)}</div>
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {tp.area}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
